import React, { useCallback } from 'react'
import Modal from 'shared/molecules/modal/Modal'
import { useAuth } from 'contexts/AuthContext'
import './NetworkErrorModal.scss'

export type NetworkErrorVariant = 'network' | 'chunk'

interface NetworkErrorModalProps {
  open: boolean
  variant: NetworkErrorVariant
  onClose?: () => void
}

const CONTENT: Record<NetworkErrorVariant, { title: string; message: string }> =
  {
    network: {
      title: 'No Internet Connection',
      message:
        'It looks like you are offline. Please check your internet connection and try again.'
    },
    chunk: {
      title: 'Application Error',
      message:
        'A part of the application failed to load. This might be due to a network issue or a recent update. Please refresh the page, or logout and try again.'
    }
  }

const NetworkErrorModal: React.FC<NetworkErrorModalProps> = ({
  open,
  variant,
  onClose
}) => {
  const { logout } = useAuth()
  const { title, message } = CONTENT[variant]

  const handleRefresh = useCallback(() => {
    window.location.reload()
  }, [])

  const handleLogout = useCallback(() => {
    onClose?.()
    logout()
  }, [logout, onClose])

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xs"
      backdrop="static"
      keyboard={false}
      dialogClassName="network-error-modal"
      body={
        <div className="network-error-modal__body">
          <div className="network-error-modal__icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {variant === 'network' ? (
                // Wifi off icon
                <>
                  <line x1="1" y1="1" x2="23" y2="23" />
                  <path d="M16.72 11.06A10.94 10.94 0 0 1 12 13a10.94 10.94 0 0 1-4.72-2.17" />
                  <path d="M21.94 9.06A16 16 0 0 0 12 5a16 16 0 0 0-9.94 4.06" />
                  <path d="M7 16l3 3 5-5" />
                </>
              ) : (
                // Alert icon (for chunk / generic error)
                <>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </>
              )}
            </svg>
          </div>
          <h3 className="network-error-modal__title">{title}</h3>
          <p className="network-error-modal__description">{message}</p>
        </div>
      }
      footer={
        <div className="network-error-modal__footer">
          <button
            type="button"
            className="network-error-modal__btn network-error-modal__btn--primary"
            onClick={handleRefresh}
          >
            Refresh Page
          </button>
          <button
            type="button"
            className="network-error-modal__btn network-error-modal__btn--secondary"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      }
    />
  )
}

export default NetworkErrorModal
